import { Box, Text, IconButton, styled, alpha } from '@ltvco/refresh-lib/theme';

import { META_BODY } from './CourtCaseReport/shared/constants';

export const ClassActionCardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isHovered',
})<{ isHovered?: boolean }>(({ theme, isHovered }) => ({
  display: 'grid',
  gridTemplateRows: 'subgrid',
  gridRow: 'span 2',
  width: '100%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  boxSizing: 'border-box',
  padding: theme.spacing(2),
  borderRadius: '8px',
  border: 'none',
  boxShadow: `0 0 0 1px ${theme.palette.divider}`,
  backgroundColor: theme.palette.common.white,
  transition: 'box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out',
  ...(isHovered
    ? {
        boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
      }
    : {}),
  '&:hover': {
    boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
}));

export const ClassActionCardHeaderRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
  minWidth: 0,
});

export const ClassActionCardMainColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '8px',
  flex: 1,
  minWidth: 0,
});

export const ClassActionDefendantName = styled(Text)(({ theme }) => ({
  display: 'block',
  fontFamily: "'Lato', sans-serif",
  fontSize: '20px',
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: 0,
  textTransform: 'none',
  color: theme.palette.text.primary,
}));

export const ClassActionCaseTitleText = styled(Text)(({ theme }) => ({
  display: 'block',
  fontFamily: `'Nunito Sans', ${theme.typography.fontFamily}`,
  fontSize: 'clamp(1.25rem, 2.2vw, 1.5rem)',
  fontWeight: 700,
  lineHeight: 1.2,
  color: theme.palette.text.primary,
}));

export const ClassActionMetaLine = styled(Text)(() => ({
  display: 'block',
  fontFamily: "'Nunito Sans', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: META_BODY,
}));

export const ClassActionMetaStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '4px',
  width: '100%',
  minWidth: 0,
});

export const ClassActionCardTopSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  minWidth: 0,
  width: '100%',
  height: '100%',
});

export const ClassActionCausePanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  alignSelf: 'stretch',
  minHeight: 0,
  gap: '8px',
  width: '100%',
  padding: theme.spacing(2),
  borderRadius: '8px',
  boxSizing: 'border-box',
  backgroundColor:
    theme.palette.mode === 'light' ? '#F8F8F8' : theme.palette.grey[900],
}));

export const ClassActionCauseHeaderRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '8px',
  width: '100%',
});

export const ClassActionCauseLabel = styled(Text)(({ theme }) => ({
  fontFamily: "'Lato', sans-serif",
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: '18px',
  letterSpacing: '0.16px',
  color: theme.palette.text.primary,
}));

export const ClassActionCauseValue = styled(Text)(() => ({
  display: 'block',
  fontFamily: "'Nunito Sans', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: META_BODY,
  width: '100%',
  textAlign: 'left',
  overflowWrap: 'break-word',
}));

export const ClassActionCausePlaceholder = styled(Text)(({ theme }) => ({
  display: 'block',
  fontFamily: "'Nunito Sans', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: theme.palette.text.secondary,
  width: '100%',
  textAlign: 'left',
}));

export const ClassActionShareIconButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  marginTop: -4,
  marginRight: -8,
  padding: 8,
  color: alpha(theme.palette.text.primary, 0.6),
  '&:hover': {
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
  },
}));

export const ClassActionGuideOuter = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2.5, 2.5),
  backgroundColor: theme.palette.common.white,
  borderRadius: '8px',
  border: 'none',
  boxShadow: `0 0 0 1px ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 2),
  },
}));

export const ClassActionGuideRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(4),
  width: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
}));

export const ClassActionGuideStepBlock = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  minWidth: 0,
}));

export const ClassActionGuideIllustration = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: 100,
  height: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  [theme.breakpoints.down('sm')]: {
    width: 80,
    height: 80,
  },
}));

export const ClassActionGuideTextStack = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  minWidth: 0,
  paddingTop: 4,
}));

export const ClassActionGuideStepTitle = styled(Box)(({ theme }) => ({
  margin: 0,
  fontFamily: `'Nunito Sans', ${theme.typography.fontFamily}`,
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  color: theme.palette.text.primary,
}));

export const ClassActionGuideStepBody = styled(Text)(() => ({
  fontFamily: "'Nunito Sans', sans-serif",
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  color: META_BODY,
}));
